import React, {useEffect} from 'react';
import {
  ActivityIndicator,
  Button,
  Divider,
  List,
  Switch,
  useTheme,
} from 'react-native-paper';
import {useMutation, useQuery} from 'react-query';
import {UserInfo} from '../../types';
import {getUserData} from '../../api';
import {priceKeys} from '../../api/billing';
import {useAppDispatch} from '../../store/hooks';
import {
  setAppDialogParams,
  setDialogLoading,
} from '../../store/slices/ui';
import {useCloseDialog} from '../../hooks/useCloseDialog';
import {setPurchasePrice} from '../../store/slices/auction';
import {PremiumDialogBody} from './premiumdialogbody';
import {
  buyServiceSmart,
  checkServiceValid,
  getStorageAddress,
  getShopServiceAddress,
  getCurrentMetamaskWalletInfo,
  uniSwapLink,
  getServicePaymentInfo,
} from '../../api/exchange';
import {SettingsAvatar} from './avatar';

const genDescription = (
  info: Awaited<ReturnType<typeof getServicePaymentInfo>> | undefined,
) => {
  const shopInterval = info?.subInterval
    ? info?.subInterval / 60 / 60 / 24
    : 0;
  const description = `${info?.value} ${info?.coinName} / ${shopInterval} days  + ${info?.value}`;
  return description + ' EXDC (One time to create a smart contract)';
};
export default function PremiumScreen() {
  const payments = useQuery(
    'premium',
    async () => {
      const user = (await getUserData()) as UserInfo;
      let isCloudEnabled = false;
      let isKioskEnabled = false;
      let storageInfo:
        | Awaited<ReturnType<typeof getServicePaymentInfo>>
        | undefined;
      let shopInfo:
        | Awaited<ReturnType<typeof getServicePaymentInfo>>
        | undefined;
      try {
        const storageAddress = await getStorageAddress();
        isCloudEnabled = await checkServiceValid(storageAddress);
        storageInfo = await getServicePaymentInfo(storageAddress);
      } catch (err) {
        console.error(err);
      }
      try {
        const shopAddress = await getShopServiceAddress();
        isKioskEnabled = await checkServiceValid(shopAddress);
        shopInfo = await getServicePaymentInfo(shopAddress);
      } catch (err) {
        console.error(err);
      }
      const paymentsInfo = {
        isCloudEnabled,
        isKioskEnabled,
        storageInfo,
        shopInfo,
      };
      const wallet = await getCurrentMetamaskWalletInfo();
      console.info('paymentsInfo', paymentsInfo);
      return {...user, ...paymentsInfo, ...wallet};
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 15000,
    },
  );
  const {closeDialog} = useCloseDialog();
  const dispatch = useAppDispatch();
  const userLoading = payments.isLoading;
  const userData = payments.data;
  const payMutation = useMutation(
    async ({service}: {service: priceKeys}) => {
      dispatch(setDialogLoading(true));
      let adr: string | undefined;
      if (service === 'Cloud') adr = await getStorageAddress();
      if (service === 'Kiosk') adr = await getShopServiceAddress();
      await buyServiceSmart(adr as string);
      await payments.refetch();
      dispatch(setDialogLoading(false));
    },
  );
  const openServiceDialog = (service: 'Cloud' | 'Kiosk') => {
    if (service === 'Cloud') {
      const info = userData?.storageInfo;
      dispatch(setPurchasePrice(genDescription(info)));
    }
    if (service === 'Kiosk') {
      const info = userData?.shopInfo;
      dispatch(setPurchasePrice(genDescription(info)));
    }
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: `Buy ${service} Service`,
        appDialogIcon: 'currency-usd',
        appDialogOnSelectRight: async () => {
          await payMutation.mutateAsync({service});
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody: PremiumDialogBody,
        appDialogControls: undefined,
        appDialogLoading: payMutation.isLoading,
      }),
    );
  };
  useEffect(() => {
    const accountsChangedListener = function () {
      payments.refetch();
    };
    window.ethereum.on('accountsChanged', accountsChangedListener);
    return () => {
      window.ethereum.removeListener(
        'accountsChanged',
        accountsChangedListener,
      );
    };
  }, []);
  const onSwitchService = (
    value: boolean,
    service: 'Cloud' | 'Kiosk',
  ) => {
    if (value) openServiceDialog(service);
  };
  const theme = useTheme();
  if (userLoading) return <ActivityIndicator size={72} />;
  return (
    <List.Section>
      <List.Item
        theme={theme}
        title={() => (
          <List.Section>
            <List.Item title={'Current Metamask Wallet:'} />
            <List.Item title={userData?.wallet?.address} />
            <Divider />
          </List.Section>
        )}
        style={{height: 'auto'}}
        left={() => (
          <SettingsAvatar
            AvatarSize={72}
            UserData={userData}
            style={{marginLeft: 20}}
          />
        )}
        description={() => (
          <List.Section>
            <List.Item
              left={() => <List.Icon icon={'wallet'} />}
              title={'EXDC: ' + (userData?.excdBalance || 0)}
              right={() => (
                <Button
                  icon={'cash-plus'}
                  mode="outlined"
                  onPress={() => window.open(uniSwapLink, '_blank')}>
                  Buy/Sell
                </Button>
              )}
            />
          </List.Section>
        )}
      />
      <Divider />
      <List.Item
        left={() => (
          <List.Icon icon={'cloud'} style={{paddingLeft: 20}} />
        )}
        title={`Special Deal Cloud Storage - 100 GB for 28 days`}
        description={genDescription(userData?.storageInfo)}
        right={() => (
          <Switch
            value={userData?.isCloudEnabled}
            onValueChange={value => onSwitchService(value, 'Cloud')}
          />
        )}
      />
      <Divider />
      <List.Item
        left={() => (
          <List.Icon icon={'cart'} style={{paddingLeft: 20}} />
        )}
        title={`Shop Special Deal - 28 days`}
        description={genDescription(userData?.shopInfo)}
        right={() => (
          <Switch
            value={userData?.isKioskEnabled}
            onValueChange={value => onSwitchService(value, 'Kiosk')}
          />
        )}
      />
    </List.Section>
  );
}
